import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { Separator } from "@/components/ui/separator";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Collapsible, CollapsibleContent, CollapsibleTrigger,
} from "@/components/ui/collapsible";
import {
  ChevronDown, ChevronRight, ChevronUp, Calculator, Timer, Lock, Layers, User, Building2, ListTodo,
} from "lucide-react";
import BusinessNameTypeahead from "@/components/BusinessNameTypeahead";
import { STATUS_COLORS, JOB_STATUSES } from "@/components/quote";

export const JobDetailsCard = ({
  job,
  setJob,
  setHasUnsavedChanges,
  expandedSections,
  toggleSection,
  deadlineCountdown,
  isAdmin,
  enableQuantityBreakMode,
  showClientDetails,
  setShowClientDetails,
  updateClient,
  onConvertToTask,
}) => {
  return (
    <Collapsible
      open={expandedSections.jobDetails}
      onOpenChange={() => toggleSection('jobDetails')}
    >
      <div className="bg-white rounded-lg border border-gray-100 shadow-sm" data-testid="job-details-card">
        <CollapsibleTrigger asChild>
          <div className="px-5 py-4 cursor-pointer hover:bg-gray-50/50 transition-colors rounded-t-lg">
            <div className="flex items-center justify-between">
              <h3 className="text-sm font-medium text-gray-700 flex items-center gap-2">
                {expandedSections.jobDetails ? <ChevronDown className="w-4 h-4 text-gray-400" /> : <ChevronRight className="w-4 h-4 text-gray-400" />}
                <Calculator className="w-4 h-4 text-blue-500" />
                Job Details
                {job.job_name && (
                  <span className="text-sm font-normal text-gray-500 ml-2">
                    – {job.job_name}
                  </span>
                )}
              </h3>
              <div className="flex items-center gap-2">
                {job.job_type === "task" && (
                  <Badge
                    className="bg-indigo-100 text-indigo-800 border-indigo-300 text-xs flex items-center gap-1"
                    data-testid="job-details-task-badge"
                  >
                    <ListTodo className="w-3 h-3" />
                    TASK
                  </Badge>
                )}
                {job.status === "Initial Contact" && job.job_type !== "task" && (
                  <button
                    type="button"
                    onClick={(e) => {
                      e.stopPropagation();
                      onConvertToTask?.();
                    }}
                    data-testid="convert-to-task-btn"
                    title="Convert this enquiry into a non-print Task"
                    className="flex items-center gap-1 px-2 py-1 rounded-md text-xs font-medium text-indigo-700 bg-indigo-50 border border-indigo-200 hover:bg-indigo-100 transition-colors"
                  >
                    <ListTodo className="w-3.5 h-3.5" />
                    Convert to Task
                  </button>
                )}
                <Badge
                  variant="outline"
                  className={`${STATUS_COLORS[job.status] || STATUS_COLORS.Draft} text-xs`}
                  data-testid="job-status-badge"
                >
                  {job.status}
                </Badge>
                {deadlineCountdown && (
                  <Badge
                    variant="outline"
                    className={`flex items-center gap-1 text-xs ${
                      deadlineCountdown.overdue
                        ? "bg-red-50 text-red-700 border-red-200"
                        : deadlineCountdown.days <= 1
                          ? "bg-amber-50 text-amber-700 border-amber-200"
                          : "bg-blue-50 text-blue-700 border-blue-200"
                    }`}
                    data-testid="deadline-countdown"
                  >
                    <Timer className="w-3 h-3" />
                    {deadlineCountdown.text}
                  </Badge>
                )}
              </div>
            </div>
          </div>
        </CollapsibleTrigger>
        <CollapsibleContent>
          <div className="px-5 pb-5 space-y-4 border-t border-gray-100">
            {/* Job Name, Quote Date, Quote Expires, Due Date, Status */}
            <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
              <div className="space-y-2">
                <Label htmlFor="job-name">Job Name</Label>
                <Input
                  id="job-name"
                  placeholder="e.g., ABC Corp Staff Uniforms"
                  value={job.job_name}
                  onChange={(e) => {
                    setHasUnsavedChanges(true);
                    setJob({ ...job, job_name: e.target.value });
                  }}
                  data-testid="job-name-input"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="quote-date">Quote Date</Label>
                <Input
                  id="quote-date"
                  type="date"
                  value={job.quote_date}
                  onChange={(e) => {
                    setHasUnsavedChanges(true);
                    setJob({ ...job, quote_date: e.target.value });
                  }}
                  data-testid="quote-date-input"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="quote-expires">Quote Expires</Label>
                <Input
                  id="quote-expires"
                  type="date"
                  value={job.deadline || ""}
                  onChange={(e) => {
                    setHasUnsavedChanges(true);
                    setJob({ ...job, deadline: e.target.value });
                  }}
                  data-testid="quote-expires-input"
                />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="due-date-mode">Due Date</Label>
                <Select
                  value={job.due_date_mode || "date"}
                  onValueChange={(v) => {
                    setHasUnsavedChanges(true);
                    setJob({
                      ...job,
                      due_date_mode: v,
                      ...(v === "date" ? { due_date_value: null } : { due_date: "" }),
                    });
                  }}
                >
                  <SelectTrigger id="due-date-mode" data-testid="due-date-mode-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="date">Specific Date</SelectItem>
                    <SelectItem value="days">Working Days</SelectItem>
                    <SelectItem value="weeks">Working Weeks</SelectItem>
                  </SelectContent>
                </Select>
                {(job.due_date_mode || "date") === "date" ? (
                  <Input
                    id="due-date"
                    type="date"
                    value={job.due_date || ""}
                    onChange={(e) => {
                      setHasUnsavedChanges(true);
                      setJob({ ...job, due_date: e.target.value });
                    }}
                    data-testid="due-date-input"
                  />
                ) : (
                  <Input
                    id="due-date-value"
                    type="number"
                    min="1"
                    placeholder={job.due_date_mode === "weeks" ? "No. of weeks" : "No. of days"}
                    value={job.due_date_value ?? ""}
                    onChange={(e) => {
                      setHasUnsavedChanges(true);
                      const raw = e.target.value;
                      const n = raw === "" ? null : Math.max(1, parseInt(raw, 10) || 1);
                      setJob({ ...job, due_date_value: n });
                    }}
                    data-testid="due-date-value-input"
                  />
                )}
                <p className="text-[10px] text-gray-400 leading-tight">
                  {(job.due_date_mode || "date") === "date"
                    ? "Fixed date the order is due"
                    : "Counted from when the invoice is marked paid"}
                </p>
              </div>
              <div className="space-y-2">
                <Label htmlFor="job-status">Status</Label>
                <Select
                  value={job.status}
                  onValueChange={(v) => {
                    setHasUnsavedChanges(true);
                    setJob({ ...job, status: v });
                  }}
                >
                  <SelectTrigger id="job-status" data-testid="job-status-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {JOB_STATUSES.map((status) => (
                      <SelectItem key={status} value={status}>
                        {status}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            {/* Internal Notes - never shown to customer */}
            <div className="space-y-2">
              <Label htmlFor="internal-notes" className="flex items-center gap-1.5">
                <Lock className="w-3.5 h-3.5 text-amber-500" />
                Internal Notes
                <span className="text-xs font-normal text-amber-600">(never shown to customer — not on PDF, emails or approval page)</span>
              </Label>
              <textarea
                id="internal-notes"
                placeholder="Private notes about this quote — e.g. customer preferences, special instructions, pricing context..."
                value={job.internal_notes || ""}
                onChange={(e) => {
                  setHasUnsavedChanges(true);
                  setJob({ ...job, internal_notes: e.target.value });
                }}
                rows={2}
                data-testid="internal-notes-input"
                className="w-full px-3 py-2 bg-amber-50/40 rounded-md border border-amber-200 text-sm resize-y focus:outline-none focus:ring-2 focus:ring-amber-400/30"
              />
            </div>

            <Separator />

            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              {isAdmin && (
                <div className="space-y-2">
                  <Label htmlFor="margin">Margin %</Label>
                  <div className="flex items-center gap-1">
                    <Input
                      id="margin"
                      type="number"
                      min="0"
                      max="100"
                      step="1"
                      value={(job.margin_percent * 100).toFixed(0)}
                      onChange={(e) => {
                        const value = parseFloat(e.target.value);
                        if (!isNaN(value) && value >= 0 && value <= 100) {
                          setHasUnsavedChanges(true);
                          setJob({
                            ...job,
                            margin_percent: value / 100,
                          });
                        }
                      }}
                      data-testid="margin-input"
                      className="font-mono flex-1"
                    />
                    <div className="flex flex-col">
                      <button
                        type="button"
                        onClick={() => {
                          const currentMargin = Math.round(job.margin_percent * 100);
                          if (currentMargin < 100) {
                            setHasUnsavedChanges(true);
                            setJob({
                              ...job,
                              margin_percent: (currentMargin + 1) / 100,
                            });
                          }
                        }}
                        className="p-0.5 hover:bg-gray-100 rounded transition-colors"
                        data-testid="margin-increase-btn"
                        title="Increase margin"
                      >
                        <ChevronUp className="w-4 h-4 text-gray-600" />
                      </button>
                      <button
                        type="button"
                        onClick={() => {
                          const currentMargin = Math.round(job.margin_percent * 100);
                          if (currentMargin > 0) {
                            setHasUnsavedChanges(true);
                            setJob({
                              ...job,
                              margin_percent: (currentMargin - 1) / 100,
                            });
                          }
                        }}
                        className="p-0.5 hover:bg-gray-100 rounded transition-colors"
                        data-testid="margin-decrease-btn"
                        title="Decrease margin"
                      >
                        <ChevronDown className="w-4 h-4 text-gray-600" />
                      </button>
                    </div>
                  </div>
                </div>
              )}
              <div className="space-y-2">
                <Label htmlFor="shipping">Shipping</Label>
                <Input
                  id="shipping"
                  type="number"
                  step="0.01"
                  value={job.shipping}
                  onChange={(e) =>
                    setJob({ ...job, shipping: parseFloat(e.target.value) || 0 })
                  }
                  data-testid="shipping-input"
                  className="font-mono"
                />
              </div>
              {isAdmin && (
                <div className="space-y-2">
                  <Label htmlFor="misc">Misc Costs</Label>
                  <Input
                    id="misc"
                    type="number"
                    step="0.01"
                    value={job.misc_costs}
                    onChange={(e) =>
                      setJob({ ...job, misc_costs: parseFloat(e.target.value) || 0 })
                    }
                    data-testid="misc-costs-input"
                    className="font-mono"
                  />
                </div>
              )}
            </div>

            <Separator />

            {/* Design Fee Section */}
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <Label className="text-sm font-medium">Design Required?</Label>
                <Switch
                  checked={job.design_required}
                  onCheckedChange={(checked) =>
                    setJob({ ...job, design_required: checked })
                  }
                  data-testid="design-required-switch"
                />
              </div>
              {job.design_required && (
                <div className="grid grid-cols-2 gap-4 pl-4 border-l-2 border-accent">
                  <div className="space-y-2">
                    <Label htmlFor="design-fee">Design Fee</Label>
                    <Input
                      id="design-fee"
                      type="number"
                      step="0.01"
                      value={job.design_fee}
                      onChange={(e) =>
                        setJob({
                          ...job,
                          design_fee: parseFloat(e.target.value) || 0,
                        })
                      }
                      data-testid="design-fee-input"
                      className="font-mono"
                    />
                  </div>
                  <div className="flex items-end gap-3">
                    <div className="flex items-center gap-2">
                      <Switch
                        checked={job.apply_margin_to_design}
                        onCheckedChange={(checked) =>
                          setJob({ ...job, apply_margin_to_design: checked })
                        }
                        data-testid="apply-margin-design-switch"
                      />
                      <Label className="text-sm">Apply Margin</Label>
                    </div>
                  </div>
                </div>
              )}
            </div>

            {/* Quantity Break Mode Toggle */}
            <Separator />
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <Label className="text-sm font-medium flex items-center gap-2">
                  <Layers className="w-4 h-4 text-purple-600" />
                  Quantity Break Mode
                </Label>
                <div className="flex items-center gap-2">
                  {job.is_quantity_break && (
                    <Badge className="bg-purple-100 text-purple-700 border-purple-300 text-xs">
                      QTY BREAK
                    </Badge>
                  )}
                  <Switch
                    checked={job.is_quantity_break}
                    onCheckedChange={enableQuantityBreakMode}
                    data-testid="quantity-break-switch"
                  />
                </div>
              </div>
              {job.is_quantity_break && (
                <p className="text-xs text-purple-600 pl-6">
                  Compare pricing across multiple quantities. Garment quantities are set globally via quantity columns.
                </p>
              )}
            </div>

            {/* Client Details (Collapsible) */}
            <Separator />
            <div className="space-y-3">
              <button
                onClick={() => setShowClientDetails(!showClientDetails)}
                className="w-full flex items-center justify-between text-sm text-muted-foreground hover:text-foreground transition-colors"
                data-testid="toggle-client-details"
              >
                <span className="flex items-center gap-2">
                  <User className="w-4 h-4" />
                  Client Details
                  {(job.client?.business_name || job.client?.contact_name || job.client?.name) && (
                    <span className="text-xs">– {job.client.business_name || job.client.contact_name || job.client.name}</span>
                  )}
                </span>
                {showClientDetails ? (
                  <ChevronUp className="w-4 h-4" />
                ) : (
                  <ChevronDown className="w-4 h-4" />
                )}
              </button>

              {showClientDetails && (
                <div className="space-y-4 p-3 bg-secondary/20 rounded-lg">
                  {/* Business Name with Typeahead - Auto-creates on save */}
                  <div className="space-y-2">
                    <Label className="text-xs flex items-center gap-2">
                      <Building2 className="w-3 h-3" />
                      Business Name
                      {job._isNewBusiness && (
                        <Badge variant="outline" className="text-[10px] bg-amber-50 text-amber-700 border-amber-300">
                          New – will create on save
                        </Badge>
                      )}
                      {job.customer_id && !job._isNewBusiness && (
                        <Badge variant="outline" className="text-[10px] bg-green-50 text-green-700 border-green-300">
                          Linked
                        </Badge>
                      )}
                    </Label>
                    <BusinessNameTypeahead
                      value={job.client?.business_name || ""}
                      customerId={job.customer_id}
                      onSelectExisting={(customer) => {
                        setHasUnsavedChanges(true);
                        setJob((prev) => ({
                          ...prev,
                          customer_id: customer.id,
                          _isNewBusiness: false,
                          client_type: customer.default_client_type || prev.client_type,
                          margin_percent: customer.default_margin_percent
                            ? customer.default_margin_percent / 100
                            : prev.margin_percent,
                          client: {
                            business_name: customer.business_name || "",
                            contact_name: customer.contact_name || "",
                            name: customer.contact_name || customer.business_name || "",
                            email: customer.email || "",
                            phone: customer.phone || "",
                            website: customer.website || "",
                            physical_address: customer.physical_address || "",
                            postal_address: customer.postal_address || "",
                          },
                        }));
                      }}
                      onCreateNew={(businessName) => {
                        setHasUnsavedChanges(true);
                        setJob((prev) => ({
                          ...prev,
                          customer_id: null,
                          _isNewBusiness: true,
                          client: {
                            ...prev.client,
                            business_name: businessName,
                          },
                        }));
                      }}
                      onChange={(value) => {
                        setHasUnsavedChanges(true);
                        updateClient("business_name", value);
                        // Mark as potential new business when typing manually
                        if (value && !job.customer_id) {
                          setJob(prev => ({ ...prev, _isNewBusiness: true }));
                        }
                      }}
                      onClear={() => {
                        setHasUnsavedChanges(true);
                        setJob((prev) => ({
                          ...prev,
                          customer_id: null,
                          _isNewBusiness: false,
                          client: {
                            ...prev.client,
                            business_name: "",
                          },
                        }));
                      }}
                    />
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label className="text-xs">Contact Name</Label>
                      <Input
                        placeholder="Contact person name"
                        value={job.client?.contact_name || ""}
                        onChange={(e) => updateClient("contact_name", e.target.value)}
                        data-testid="client-contact-name-input"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label className="text-xs">Email</Label>
                      <Input
                        type="email"
                        placeholder="email@example.com"
                        value={job.client?.email || ""}
                        onChange={(e) => updateClient("email", e.target.value)}
                        data-testid="client-email-input"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label className="text-xs">Phone</Label>
                      <Input
                        type="tel"
                        placeholder="+61 4XX XXX XXX"
                        value={job.client?.phone || ""}
                        onChange={(e) => updateClient("phone", e.target.value)}
                        data-testid="client-phone-input"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label className="text-xs">Website</Label>
                      <Input
                        placeholder="www.example.com"
                        value={job.client?.website || ""}
                        onChange={(e) => updateClient("website", e.target.value)}
                        data-testid="client-website-input"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label className="text-xs">Physical Address</Label>
                      <Input
                        placeholder="Street address"
                        value={job.client?.physical_address || ""}
                        onChange={(e) => updateClient("physical_address", e.target.value)}
                        data-testid="client-physical-address-input"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label className="text-xs">Postal Address</Label>
                      <Input
                        placeholder="Postal/mailing address (if different)"
                        value={job.client?.postal_address || ""}
                        onChange={(e) => updateClient("postal_address", e.target.value)}
                        data-testid="client-postal-address-input"
                      />
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        </CollapsibleContent>
      </div>
    </Collapsible>
  );
};
